import React, { useCallback, useState, useEffect } from 'react';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader, Sparkle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import toast from 'react-hot-toast';
import z from 'zod';

import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { LogoGenService } from '../services/logo-gen.api';
import { saveFile, saveLogoInfo, setFileName } from 'store/slices/logoGen';
import { colorMap, COLORS, INDUSTRIES, LOGO_STYLES, TONES } from '../constants/data';

const schema = z.object({
  company_name: z.string().nonempty({ message: 'Please enter a company name' }),
  company_description: z.string().min(5, {
    message: 'Company description must be at least 5 characters',
  }),
  style: z.string().nonempty({ message: 'Please select a style' }),
  industry: z.string().nonempty({ message: 'Please select an industry' }),
  tone: z.string().nonempty({ message: 'Please select a tone of voice' }),
  color: z.string().nonempty({ message: 'Please select a color' }),
  negative_prompt: z.string().optional(),
  token: z.string().optional(),
});

type TSchema = z.infer<typeof schema>;

export default function LogoGenerateForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const form = useForm<TSchema>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      company_name: '',
      company_description: '',
      negative_prompt: '',
      industry: '',
      tone: '',
      color: 'None',
      style: ''
    }
  });

  const { formState: { errors, isValid }, setValue, watch } = form;
  const selectedColor = watch('color');

  const { mutate, isPending } = useMutation({
    mutationKey: ['generate-logo-from-text'],
    mutationFn: LogoGenService.generateLogoFromText,
    onSuccess: (data) => {
      dispatch(
        saveLogoInfo({
          tempLogoUrl: data.data.logo,
          style: form.getValues('style'),
          companyName: form.getValues('company_name'),
          companyDescription: form.getValues('company_description'),
          industry: form.getValues('industry'),
          tone: form.getValues('tone'),
          color: form.getValues('color'),
          negativePrompt: "", // no negative prompt required
        })
      );
      dispatch(saveFile({ fileSaved: false }));
      dispatch(setFileName({ fileName: 'Untitled 1' }));
      navigate(`/text-to-logo/result`);
    },
    onError: () => {
      toast.error('Failed to generate logo. Please try again.', {
        position: 'bottom-center',
      });
    },
  });

  const onVerify = useCallback((token: string) => {
    try {
      setRecaptchaToken(token);
      setValue('token', token);
    } catch (error) {
      toast.error('Error setting reCAPTCHA token.');
      console.error('Error setting reCAPTCHA token:', error);
    }
  }, [setValue]);

  // Force ReCAPTCHA to reload
  useEffect(() => {
    setRefreshReCaptcha(true);
    const timer = setTimeout(() => {
      setRefreshReCaptcha(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const onSubmit = (values: TSchema) => {
    try {
      mutate(values);
      // Refresh ReCAPTCHA after submission
      setRefreshReCaptcha(true);
      setTimeout(() => setRefreshReCaptcha(false), 100);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <div className="max-w-3xl mx-auto text-text-primary">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Company Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Company name</label>
            <Input
              {...form.register('company_name')}
              placeholder="Enter name here"
              className="w-full p-3 rounded-lg border border-gray-200"
            />
            {errors.company_name && (
              <p className="text-red-500 text-xs">{errors.company_name.message}</p>
            )}
          </div>

          {/* Company Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Company description</label>
            <Input
              {...form.register('company_description')}
              placeholder="Enter description here"
              className="w-full p-3 rounded-lg border border-gray-200"
            />
            {errors.company_description && (
              <p className="text-red-500 text-xs">{errors.company_description.message}</p>
            )}
          </div>

          {/* Industry, Tone, and Color in one row */}
          <div className="flex flex-col lg:flex-row w-full gap-4">
            <div className="lg:flex-1 space-y-2 w-full">
              <label className="text-sm font-medium">Industry</label>
              <Select onValueChange={(value) => setValue('industry', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map(industry => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.industry && (
                <p className="text-red-500 text-xs">{errors.industry.message}</p>
              )}
            </div>

            {/* Container for Tone and Color - Flex on both mobile and desktop */}
            <div className="flex gap-4 w-full lg:w-auto lg:flex-1">
              {/* Tone - Flex-1 within its container */}

              <div className="flex-1 space-y-2">
                <label className="text-sm font-medium text-nowrap">Tone of Voice</label>
                <Select onValueChange={(value) => setValue('tone', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {TONES.map(tone => (
                      <SelectItem key={tone} value={tone}>
                        {tone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.tone && (
                  <p className="text-red-500 text-xs">{errors.tone.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Colour</label>
                <Select onValueChange={(value) => setValue('color', value)}>
                  <SelectTrigger className="w-18 px-2 flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded border border-gray-200 flex-shrink-0"
                      style={{
                        backgroundColor: colorMap[selectedColor],
                        borderColor: selectedColor === 'White' ? '#e5e7eb' : undefined
                      }}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {COLORS.map((color) => (
                      <SelectItem 
                        key={color} 
                        value={color}
                        className="flex items-center gap-2"
                      >
                        <div
                          className="w-4 h-4 rounded border border-gray-200 flex-shrink-0"
                          style={{
                            backgroundColor: colorMap[color],
                            borderColor: color === 'White' ? '#e5e7eb' : undefined
                          }}
                        />
                        <span>{color}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.color && (
                  <p className="text-red-500 text-xs">{errors.color.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Style Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Select style</label>
            <div className="flex lg:grid lg:grid-cols-4 gap-4 overflow-x-auto pb-2 lg:mx-0 lg:px-0 lg:overflow-x-visible">
              <Controller
                name="style"
                control={form.control}
                render={({ field }) => (
                  <>
                    {LOGO_STYLES.map((style) => (
                      <button
                        key={style.id}
                        type="button"
                        onClick={() => field.onChange(style.id)}
                        className={`relative flex-shrink-0 w-32 lg:w-auto aspect-square rounded-lg border overflow-hidden transition-all ${
                          field.value === style.id
                            ? 'border-2 border-primary ring-2 ring-purple-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={style.imagePath}
                          alt={style.text}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white py-2 px-3 text-xs text-center">
                          {style.text}
                        </div>
                      </button>
                    ))}
                  </>
                )}
              />
            </div>
            {errors.style && (
              <p className="text-red-500 text-xs">{errors.style.message}</p>
            )}
          </div>

          {/* Generate Button */}
          <Button
            variant={'customPrimary'}
            type="submit"
            disabled={!isValid || isPending}
            className="w-full py-4"
          >
            {isPending ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Sparkle className="w-5 h-5" />
                <span>Generate logo</span>
              </>
            )}
          </Button>
        </form>
      </FormProvider>

      {!refreshReCaptcha && (
        <GoogleReCaptcha 
          key={refreshReCaptcha ? 'refresh' : 'initial'}
          action="KaiRecaptcha0911" 
          onVerify={onVerify} 
        />
      )}
    </div>
  );
}